// Records → Coverage Map. Plots every completed delivery in the
// caller's visible set as a Mapbox point, with clustering by default
// and optional aggregation buckets by city or ZIP.
//
// This is the LYN Rx port of ~/Desktop/cnc-web/src/components/dispatch/
// CoverageMap.jsx (v1). Same UX, same visual scale, same filter set —
// the reference implementation is the design contract. Divergences
// from CNC:
//   1. Multi-tenant. The pharmacy dropdown is derived from a
//      pharmacies query scoped by RLS instead of a hardcoded
//      SHSP/Aultman list.
//   2. Data comes from /api/coverage-map (this repo), which
//      authenticates the caller via authenticateRevealCaller and
//      returns an RLS-scoped fetch through a token-bound client.
//   3. TypeScript + Tailwind + LYN's mapbox token env
//      (VITE_MAPBOX_ACCESS_TOKEN) — the CNC file was JSX with inline
//      styles + VITE_MAPBOX_TOKEN.
//
// Native GeoJSON sources + Mapbox clustering (not HTML markers) —
// clustering here matters because a mature pharmacy could have
// thousands of deliveries; HTML markers don't scale past ~500. Two
// sources are wired: `stops` (individual pins with cluster mode) and
// `agg` (aggregated bubbles per city or per ZIP). Layer visibility
// toggles based on the groupBy filter.

import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, Point } from 'geojson';
import { useAuth } from '../../lib/auth/auth-context';
import { supabase as defaultClient } from '../../lib/supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import type { CoverageStop } from './coverage-map-types';

// ---------------------------------------------------------------
// Constants
// ---------------------------------------------------------------

const MAP_STYLES = [
  { key: 'dark', label: 'Dark', url: 'mapbox://styles/mapbox/dark-v11' },
  { key: 'light', label: 'Light', url: 'mapbox://styles/mapbox/light-v11' },
  { key: 'streets', label: 'Streets', url: 'mapbox://styles/mapbox/streets-v12' },
  { key: 'satellite', label: 'Satellite', url: 'mapbox://styles/mapbox/satellite-streets-v12' },
] as const;

const RANGES = [
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
  { key: 'custom', label: 'Custom' },
] as const;

type RangeKey = (typeof RANGES)[number]['key'];

const DOW_OPTIONS = [
  { n: 1, label: 'Mon' },
  { n: 2, label: 'Tue' },
  { n: 3, label: 'Wed' },
  { n: 4, label: 'Thu' },
  { n: 5, label: 'Fri' },
] as const;

const DEMO_STYLE_URL = 'https://demotiles.maplibre.org/style.json';

// Akron, OH — LYN's initial operating region. Matches DispatchMap so
// users switching between maps see the same default frame.
const INITIAL_CENTER: [number, number] = [-81.52, 40.95];
const INITIAL_ZOOM = 9.2;

// ---------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------

function resolveStyleUrl(preferredIdx: number): string {
  const key = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
  if (!key) {
    // eslint-disable-next-line no-console
    console.warn(
      '[CoverageMap] VITE_MAPBOX_ACCESS_TOKEN not set; falling back to low-fidelity demo tiles.',
    );
    mapboxgl.accessToken = '';
    return DEMO_STYLE_URL;
  }
  mapboxgl.accessToken = key;
  const style = MAP_STYLES[preferredIdx] ?? MAP_STYLES[0];
  // Safe: MAP_STYLES has ≥1 entry, so ?? MAP_STYLES[0] always resolves.
  return (style as { url: string }).url;
}

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function daysAgoStr(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Parse YYYY-MM-DD as a local date so day-of-week isn't off-by-one in
// timezones east of UTC. Matches CNC's dayOfWeekFromDate.
function dayOfWeekFromDate(iso: string | null): number | null {
  if (!iso) return null;
  const parts = String(iso).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number) as [number, number, number];
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getDay();
}

// Inline city normalizer. Ported from CNC's normalizeCity (~/Desktop/
// cnc-web/src/lib/normalizeCity.js). Matters here because the "By city"
// aggregation groups on the normalized string — without this,
// "N CANTON" and "North Canton" would appear as two separate bubbles.
const CITY_ABBREV: Record<string, string> = {
  'NEW PHILA': 'New Philadelphia',
  'N CANTON': 'North Canton',
  'N. CANTON': 'North Canton',
  'NO CANTON': 'North Canton',
  'E CANTON': 'East Canton',
  'E. CANTON': 'East Canton',
  'W CANTON': 'West Canton',
  'W. CANTON': 'West Canton',
  'S CANTON': 'South Canton',
  'S. CANTON': 'South Canton',
  'CUY FALLS': 'Cuyahoga Falls',
  'CUYA FALLS': 'Cuyahoga Falls',
  'CUYAHOGA FLS': 'Cuyahoga Falls',
  'C FALLS': 'Cuyahoga Falls',
  'MUN FALLS': 'Munroe Falls',
  'SILVER LK': 'Silver Lake',
  'FAIR LAWN': 'Fairlawn',
  'BATH TWP': 'Bath',
  'COPLEY TWP': 'Copley',
  'NEW FRANK': 'New Franklin',
  'BOSTON HTS': 'Boston Heights',
};
const CITY_LOWER = new Set(['of', 'the', 'and', 'a', 'an', 'in', 'on']);
function cityTitleCase(str: string): string {
  return str.toLowerCase().replace(/[\p{L}\p{N}']+/gu, (word, idx) => {
    if (idx > 0 && CITY_LOWER.has(word)) return word;
    return word.charAt(0).toUpperCase() + word.slice(1);
  });
}
function normalizeCity(raw: string | null | undefined): string {
  if (!raw) return '';
  const trimmed = String(raw).trim().replace(/\s+/g, ' ');
  if (!trimmed) return '';
  const upper = trimmed.toUpperCase();
  if (CITY_ABBREV[upper]) return CITY_ABBREV[upper] as string;
  return cityTitleCase(trimmed);
}

// Minimal HTML escape for popup content. Popup text is user-controlled
// data (city / zip / pharmacy names come from CSV upload); this
// prevents any injection into the popup DOM via crafted rows.
function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ---------------------------------------------------------------
// GeoJSON builders + layer install/interaction wiring
// ---------------------------------------------------------------

type StopFeatureProps = {
  driver: string | null;
  date: string | null;
  cold: boolean;
  city: string | null;
  zip: string | null;
  pharmacy: string | null;
  status: string | null;
};

type StopFeature = Feature<Point, StopFeatureProps>;

type AggFeatureProps = {
  label: string;
  count: number;
  cold: number;
};

type AggFeature = Feature<Point, AggFeatureProps>;

function stopsFeatureCollection(stops: CoverageStop[]): FeatureCollection<Point, StopFeatureProps> {
  const features: StopFeature[] = stops.map((s) => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: [s.lng, s.lat] },
    properties: {
      driver: s.driver,
      date: s.date,
      cold: s.cold,
      city: s.city,
      zip: s.zip,
      pharmacy: s.pharmacy,
      status: s.status,
    },
  }));
  return { type: 'FeatureCollection', features };
}

// Install both sources + all six layers. Called on initial map load and
// after every setStyle swap (which wipes all sources/layers).
function installLayers(map: mapboxgl.Map): void {
  map.addSource('stops', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
    cluster: true,
    clusterRadius: 45,
    clusterMaxZoom: 14,
  });
  map.addSource('agg', {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  });

  // Cluster mode layers ---------------------------------------------
  map.addLayer({
    id: 'clusters',
    type: 'circle',
    source: 'stops',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        '#60A5FA',
        25,
        '#2563eb',
        100,
        '#1E3A8A',
        500,
        '#0A2463',
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        16,
        25,
        22,
        100,
        28,
        500,
        34,
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    },
  });
  map.addLayer({
    id: 'cluster-count',
    type: 'symbol',
    source: 'stops',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': ['get', 'point_count_abbreviated'],
      'text-size': 12,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    },
    paint: { 'text-color': '#fff' },
  });
  map.addLayer({
    id: 'stop-point',
    type: 'circle',
    source: 'stops',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': ['case', ['get', 'cold'], '#60A5FA', '#0A2463'],
      'circle-radius': 5,
      'circle-stroke-width': 1.5,
      'circle-stroke-color': '#fff',
    },
  });

  // Aggregated mode layers ------------------------------------------
  map.addLayer({
    id: 'agg-circle',
    type: 'circle',
    source: 'agg',
    layout: { visibility: 'none' },
    paint: {
      'circle-color': [
        'step',
        ['get', 'count'],
        '#60A5FA',
        25,
        '#2563eb',
        100,
        '#1E3A8A',
        500,
        '#0A2463',
      ],
      'circle-radius': [
        'step',
        ['get', 'count'],
        18,
        25,
        24,
        100,
        32,
        500,
        40,
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    },
  });
  map.addLayer({
    id: 'agg-count',
    type: 'symbol',
    source: 'agg',
    layout: {
      visibility: 'none',
      'text-field': ['to-string', ['get', 'count']],
      'text-size': 12,
      'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
    },
    paint: { 'text-color': '#fff' },
  });
  map.addLayer({
    id: 'agg-label',
    type: 'symbol',
    source: 'agg',
    layout: {
      visibility: 'none',
      'text-field': ['get', 'label'],
      'text-size': 11,
      'text-offset': [0, 1.6],
      'text-anchor': 'top',
      'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
      'text-allow-overlap': false,
    },
    paint: {
      'text-color': '#fff',
      'text-halo-color': '#0D1B2A',
      'text-halo-width': 1.5,
    },
  });
}

function applyGroupVisibility(map: mapboxgl.Map, mode: GroupBy): void {
  const clusterOn = mode === 'cluster';
  const setVis = (id: string, on: boolean): void => {
    if (map.getLayer(id)) {
      map.setLayoutProperty(id, 'visibility', on ? 'visible' : 'none');
    }
  };
  setVis('clusters', clusterOn);
  setVis('cluster-count', clusterOn);
  setVis('stop-point', clusterOn);
  setVis('agg-circle', !clusterOn);
  setVis('agg-count', !clusterOn);
  setVis('agg-label', !clusterOn);
}

function wireInteractions(map: mapboxgl.Map): void {
  // Cluster click → zoom into cluster expansion. mapbox-gl's typed
  // GeoJSONFeature has properties + geometry only via unknown, so we
  // re-cast to the plain GeoJSON Feature<Point, …> shape our sources
  // actually emit.
  map.on('click', 'clusters', (e) => {
    const features = map.queryRenderedFeatures(e.point, { layers: ['clusters'] });
    const raw = features[0];
    if (!raw) return;
    const f = raw as unknown as Feature<Point, { cluster_id?: number }>;
    const source = map.getSource('stops') as unknown as {
      getClusterExpansionZoom: (
        clusterId: number,
        cb: (err: Error | null, zoom: number) => void,
      ) => void;
    };
    const clusterId = f.properties?.cluster_id;
    if (typeof clusterId !== 'number') return;
    source.getClusterExpansionZoom(clusterId, (err, zoom) => {
      if (err) return;
      const coords = f.geometry.coordinates as [number, number];
      map.easeTo({ center: coords, zoom });
    });
  });

  // Individual stop click → popup with driver / date / city / pharmacy.
  map.on('click', 'stop-point', (e) => {
    const raw = e.features?.[0];
    if (!raw) return;
    const f = raw as unknown as StopFeature;
    const p = f.properties;
    const coords = f.geometry.coordinates as [number, number];
    const driver = escapeHtml(String(p.driver ?? '—'));
    const date = escapeHtml(String(p.date ?? ''));
    const cold = p.cold === true;
    const city = escapeHtml(String(p.city ?? ''));
    const zip = escapeHtml(String(p.zip ?? ''));
    const pharmacy = escapeHtml(String(p.pharmacy ?? ''));
    const status = escapeHtml(String(p.status ?? ''));
    new mapboxgl.Popup({ offset: 12 })
      .setLngLat(coords)
      .setHTML(
        `<div style="font-family:Inter,sans-serif;font-size:12px;line-height:1.5;color:#0D1B2A">
          <div style="font-weight:700;margin-bottom:4px">${driver}</div>
          <div>${date}${cold ? ' · ❄️ Cold chain' : ''}</div>
          <div style="color:#6B7280">${city}${zip ? ' ' + zip : ''}</div>
          <div style="color:#6B7280">${pharmacy}${status ? ' · ' + status : ''}</div>
        </div>`,
      )
      .addTo(map);
  });

  // Aggregated bubble click → count + cold breakdown.
  map.on('click', 'agg-circle', (e) => {
    const raw = e.features?.[0];
    if (!raw) return;
    const f = raw as unknown as AggFeature;
    const p = f.properties;
    const coords = f.geometry.coordinates as [number, number];
    const label = escapeHtml(String(p.label ?? ''));
    const count = Number(p.count ?? 0);
    const cold = Number(p.cold ?? 0);
    new mapboxgl.Popup({ offset: 12 })
      .setLngLat(coords)
      .setHTML(
        `<div style="font-family:Inter,sans-serif;font-size:13px;line-height:1.5;color:#0D1B2A">
          <div style="font-weight:700;margin-bottom:2px">${label}</div>
          <div>${count} stops${cold ? ' · ' + cold + ' cold-chain' : ''}</div>
        </div>`,
      )
      .addTo(map);
  });

  const cursorOn = ['clusters', 'stop-point', 'agg-circle'];
  for (const id of cursorOn) {
    map.on('mouseenter', id, () => {
      map.getCanvas().style.cursor = 'pointer';
    });
    map.on('mouseleave', id, () => {
      map.getCanvas().style.cursor = '';
    });
  }
}

// ---------------------------------------------------------------
// Component
// ---------------------------------------------------------------

type GroupBy = 'cluster' | 'city' | 'zip';

export type CoverageMapProps = {
  // Test seam — defaults to the singleton browser supabase client.
  client?: LynrxSupabaseClient;
  // Test seam — defaults to global fetch. Injected in tests so we can
  // avoid a real network call.
  fetchFn?: typeof fetch;
};

type PharmacyOption = { id: string; name: string };

export function CoverageMap({
  client = defaultClient,
  fetchFn,
}: CoverageMapProps = {}): JSX.Element {
  const { session } = useAuth();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [styleIdx, setStyleIdx] = useState(0);

  // Filters ---------------------------------------------------------
  const [rangeKey, setRangeKey] = useState<RangeKey>('30');
  const [customStart, setCustomStart] = useState(daysAgoStr(30));
  const [customEnd, setCustomEnd] = useState(todayStr());
  const [pharmacy, setPharmacy] = useState<string>('all');
  const [coldOnly, setColdOnly] = useState(false);
  const [driverFilter, setDriverFilter] = useState<Set<string>>(new Set());
  const [dowFilter, setDowFilter] = useState<Set<number>>(new Set());
  const [groupBy, setGroupBy] = useState<GroupBy>('cluster');

  // Data ------------------------------------------------------------
  const [stops, setStops] = useState<CoverageStop[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [pharmacyOptions, setPharmacyOptions] = useState<PharmacyOption[]>([]);

  const { startDate, endDate } = useMemo(() => {
    if (rangeKey === 'custom') return { startDate: customStart, endDate: customEnd };
    const n = parseInt(rangeKey, 10);
    return { startDate: daysAgoStr(n), endDate: todayStr() };
  }, [rangeKey, customStart, customEnd]);

  // ---------------------------------------------------------------
  // Load pharmacy dropdown options once. RLS scopes the visible set —
  // pharmacy_admin sees their tenant's pharmacies, dispatchers see
  // ones they have pharmacy_access to, platform_admin sees all.
  // ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function loadPharmacies(): Promise<void> {
      const { data, error: pErr } = await client
        .from('pharmacies')
        .select('id, name')
        .is('deleted_at', null)
        .order('name', { ascending: true });
      if (cancelled) return;
      if (pErr || !data) return; // Fail quiet — dropdown just shows "All".
      const rows = data as PharmacyOption[];
      setPharmacyOptions(rows);
    }
    void loadPharmacies();
    return () => {
      cancelled = true;
    };
  }, [client]);

  // ---------------------------------------------------------------
  // Fetch stops whenever the server-side filters change. Client-side
  // filters (driver / dow) narrow the fetched set below without
  // re-hitting the API.
  // ---------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const token = session?.access_token;
    if (!token) return; // Auth not ready yet; useEffect re-fires when session lands.
    async function run(): Promise<void> {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({ start: startDate, end: endDate });
        if (pharmacy !== 'all') params.set('pharmacy', pharmacy);
        if (coldOnly) params.set('cold', '1');
        const doFetch = fetchFn ?? fetch;
        const r = await doFetch(`/api/coverage-map?${params.toString()}`, {
          method: 'GET',
          headers: { authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error(`Fetch failed (${r.status})`);
        const j = (await r.json()) as { stops: CoverageStop[]; truncated: boolean };
        if (cancelled) return;
        setStops(j.stops ?? []);
        setTruncated(Boolean(j.truncated));
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Unknown error');
          setStops([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void run();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate, pharmacy, coldOnly, session?.access_token, fetchFn]);

  // Driver pills — derived from whatever's currently in the fetched set.
  const driverOptions = useMemo(() => {
    const s = new Set<string>();
    for (const x of stops) if (x.driver) s.add(x.driver);
    return Array.from(s).sort();
  }, [stops]);

  // Client-side narrow by driver + day-of-week pill selections.
  const visibleStops = useMemo(() => {
    return stops.filter((s) => {
      if (driverFilter.size > 0 && (!s.driver || !driverFilter.has(s.driver))) return false;
      if (dowFilter.size > 0) {
        const dow = dayOfWeekFromDate(s.date);
        if (dow === null || !dowFilter.has(dow)) return false;
      }
      return true;
    });
  }, [stops, driverFilter, dowFilter]);

  // Aggregation buckets for "By city" / "By ZIP" modes.
  const aggregatedFeatures = useMemo<AggFeature[]>(() => {
    if (groupBy === 'cluster') return [];
    const groups = new Map<
      string,
      { label: string; count: number; cold: number; latSum: number; lngSum: number }
    >();
    for (const s of visibleStops) {
      let key: string;
      let label: string;
      if (groupBy === 'city') {
        const c = normalizeCity(s.city);
        if (!c) continue;
        key = c.toUpperCase();
        label = c;
      } else {
        if (!s.zip) continue;
        key = String(s.zip).trim();
        label = key;
      }
      let g = groups.get(key);
      if (!g) {
        g = { label, count: 0, cold: 0, latSum: 0, lngSum: 0 };
        groups.set(key, g);
      }
      g.count += 1;
      if (s.cold) g.cold += 1;
      g.latSum += s.lat;
      g.lngSum += s.lng;
    }
    return Array.from(groups.values()).map((g) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [g.lngSum / g.count, g.latSum / g.count],
      },
      properties: { label: g.label, count: g.count, cold: g.cold },
    }));
  }, [visibleStops, groupBy]);

  // Init map once. Style swap is handled in a separate effect so this
  // one doesn't need to re-run and thrash the container.
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: resolveStyleUrl(styleIdx),
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
    });
    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    map.on('load', () => {
      installLayers(map);
      wireInteractions(map);
      mapRef.current = map;
      setMapReady(true);
    });
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refs so the post-style-swap re-hydrate reads the freshest data +
  // groupBy without needing them in the swap effect's dep list (which
  // would rebuild the entire map on any data change).
  const visibleStopsRef = useRef(visibleStops);
  const aggregatedFeaturesRef = useRef(aggregatedFeatures);
  const groupByRef = useRef(groupBy);
  useEffect(() => {
    visibleStopsRef.current = visibleStops;
  }, [visibleStops]);
  useEffect(() => {
    aggregatedFeaturesRef.current = aggregatedFeatures;
  }, [aggregatedFeatures]);
  useEffect(() => {
    groupByRef.current = groupBy;
  }, [groupBy]);

  // Style swap — reinstall sources/layers + re-push data + restore
  // visibility. Guarded by appliedStyleIdx so the initial mapReady flip
  // doesn't trigger a spurious wipe of the just-installed layers.
  const appliedStyleIdx = useRef(0);
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (appliedStyleIdx.current === styleIdx) return;
    appliedStyleIdx.current = styleIdx;
    const map = mapRef.current;
    map.setStyle(resolveStyleUrl(styleIdx));
    map.once('style.load', () => {
      installLayers(map);
      wireInteractions(map);
      const stopsSrc = map.getSource('stops') as mapboxgl.GeoJSONSource | undefined;
      const aggSrc = map.getSource('agg') as mapboxgl.GeoJSONSource | undefined;
      stopsSrc?.setData(stopsFeatureCollection(visibleStopsRef.current));
      aggSrc?.setData({
        type: 'FeatureCollection',
        features: aggregatedFeaturesRef.current,
      });
      applyGroupVisibility(map, groupByRef.current);
    });
  }, [styleIdx, mapReady]);

  // Push individual-stops data updates into the cluster source.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const src = mapRef.current.getSource('stops') as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData(stopsFeatureCollection(visibleStops));
  }, [visibleStops, mapReady]);

  // Push aggregated data into the agg source.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    const src = mapRef.current.getSource('agg') as mapboxgl.GeoJSONSource | undefined;
    if (!src) return;
    src.setData({ type: 'FeatureCollection', features: aggregatedFeatures });
  }, [aggregatedFeatures, mapReady]);

  // Toggle which set of layers is visible when the group-by mode
  // changes. Cluster mode → individual pins + cluster bubbles.
  // City/ZIP → aggregated bubbles.
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    applyGroupVisibility(mapRef.current, groupBy);
  }, [groupBy, mapReady]);

  function toggleDriver(name: string): void {
    setDriverFilter((prev) => {
      const n = new Set(prev);
      if (n.has(name)) n.delete(name);
      else n.add(name);
      return n;
    });
  }
  function toggleDow(d: number): void {
    setDowFilter((prev) => {
      const n = new Set(prev);
      if (n.has(d)) n.delete(d);
      else n.add(d);
      return n;
    });
  }

  const coldCount = useMemo(() => visibleStops.filter((s) => s.cold).length, [visibleStops]);

  return (
    <div className="flex h-[calc(100vh-3rem)] flex-col gap-4 overflow-hidden p-6">
      {/* Header + primary filters --------------------------------- */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <div className="text-xl font-bold text-ink">Coverage Map</div>
          <div className="mt-0.5 text-xs text-ink-3">
            {loading
              ? 'Loading…'
              : `${visibleStops.length.toLocaleString()} stops` +
                (coldCount ? ` · ${coldCount.toLocaleString()} cold-chain` : '') +
                (truncated ? ' · results capped' : '')}
          </div>
        </div>
        <div className="flex-1" />
        <FilterGroup label="Range">
          <select
            value={rangeKey}
            onChange={(e) => setRangeKey(e.target.value as RangeKey)}
            style={selectStyle}
          >
            {RANGES.map((r) => (
              <option key={r.key} value={r.key}>
                {r.label}
              </option>
            ))}
          </select>
        </FilterGroup>
        {rangeKey === 'custom' && (
          <>
            <FilterGroup label="From">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                style={selectStyle}
              />
            </FilterGroup>
            <FilterGroup label="To">
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                style={selectStyle}
              />
            </FilterGroup>
          </>
        )}
        <FilterGroup label="Pharmacy">
          <select value={pharmacy} onChange={(e) => setPharmacy(e.target.value)} style={selectStyle}>
            <option value="all">All</option>
            {pharmacyOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </FilterGroup>
        <label className="flex cursor-pointer items-center gap-1.5 text-xs text-ink">
          <input
            type="checkbox"
            checked={coldOnly}
            onChange={(e) => setColdOnly(e.target.checked)}
          />
          Cold-chain only
        </label>
        <FilterGroup label="Group by">
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            style={selectStyle}
          >
            <option value="cluster">Cluster</option>
            <option value="city">City</option>
            <option value="zip">ZIP</option>
          </select>
        </FilterGroup>
        <FilterGroup label="Map">
          <select
            value={styleIdx}
            onChange={(e) => setStyleIdx(parseInt(e.target.value, 10))}
            style={selectStyle}
          >
            {MAP_STYLES.map((s, i) => (
              <option key={s.key} value={i}>
                {s.label}
              </option>
            ))}
          </select>
        </FilterGroup>
      </div>

      {/* Driver pill row. Only renders once we have drivers to show. */}
      {driverOptions.length > 0 && (
        <div className="flex max-h-16 flex-wrap gap-1.5 overflow-y-auto pb-0.5">
          <button
            type="button"
            onClick={() => setDriverFilter(new Set())}
            style={pillStyle(driverFilter.size === 0)}
          >
            All drivers
          </button>
          {driverOptions.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => toggleDriver(name)}
              style={pillStyle(driverFilter.has(name))}
            >
              {name}
            </button>
          ))}
        </div>
      )}

      {/* Day-of-week pill row ------------------------------------- */}
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="mr-1 text-[11px] font-semibold uppercase tracking-wider text-ink-3">
          Day of week
        </span>
        <button
          type="button"
          onClick={() => setDowFilter(new Set())}
          style={pillStyle(dowFilter.size === 0)}
        >
          All days
        </button>
        {DOW_OPTIONS.map(({ n, label }) => (
          <button
            key={n}
            type="button"
            onClick={() => toggleDow(n)}
            style={pillStyle(dowFilter.has(n))}
          >
            {label}
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 px-3 py-3 text-xs text-red-800">{error}</div>
      )}

      <div
        ref={containerRef}
        data-testid="coverage-map-container"
        className="flex-1 overflow-hidden rounded-xl border border-black/10"
      />
    </div>
  );
}

// ---------------------------------------------------------------
// Small UI primitives — kept co-located so the file stands alone as
// a single-purpose surface. Match CNC's inline-styled look because
// the design contract is 1:1 parity with the reference implementation.
// ---------------------------------------------------------------

function FilterGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-ink-3">
        {label}
      </span>
      {children}
    </div>
  );
}

const selectStyle: CSSProperties = {
  padding: '6px 10px',
  borderRadius: 8,
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'white',
  color: '#0D1B2A',
  fontSize: 13,
  fontFamily: 'Inter, sans-serif',
  cursor: 'pointer',
};

function pillStyle(active: boolean): CSSProperties {
  return {
    padding: '4px 12px',
    borderRadius: 999,
    border: `1px solid ${active ? '#0A2463' : 'rgba(0,0,0,0.12)'}`,
    background: active ? '#0A2463' : 'white',
    color: active ? 'white' : '#0D1B2A',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
  };
}
